import { useIntl } from 'react-intl';
import welcomeIllustration from '@repo/visual-assets/illustrations/welcome.png';
import welcomeInvoicingIllustration from '@repo/visual-assets/illustrations/welcome-invoicing.png';
import {
  useDougsPartnerName,
  useIsCobrandedDougs,
} from 'register-partners-js/react/hooks/use-is-cobranded-dougs';

interface UseWelcomeContentParams {
  legalFlow: string;
  ownerFirstName?: string;
  organizationName?: string;
  isEmbedded?: boolean;
  isAccountant?: boolean;
  partnerName?: string;
  companyCreationFromScratch?: boolean;
}

interface UseWelcomeContentResult {
  title: string;
  subtitle: string;
  featuresList: string[];
  illustrationSrc: string;
  ctaLabel: string;
  socialProofLabel: string;
  shouldShowPromoBanner: boolean;
}

export function useWelcomeContent({
  legalFlow,
  ownerFirstName,
  organizationName,
  isEmbedded,
  isAccountant,
  partnerName,
  companyCreationFromScratch = false,
}: UseWelcomeContentParams): UseWelcomeContentResult {
  const { formatMessage } = useIntl();

  const isExistingCompanyFlow = legalFlow === 'existing_company';
  const isQontoInvoicingFlow = legalFlow === 'qonto_invoicing';
  const isLegalPlaceFastTrack =
    !companyCreationFromScratch && partnerName?.toLowerCase() === 'legalplace';
  const shouldShowPromoBanner = partnerName?.toLowerCase().includes('beglaubigt') ?? false;
  const isCobrandedDougs = useIsCobrandedDougs();
  const dougsDisplayName = useDougsPartnerName();

  const title = (() => {
    if (isQontoInvoicingFlow) {
      return formatMessage({ id: 'qonto-pa.welcome.title' }, { owner_first_name: ownerFirstName });
    }
    if (isCobrandedDougs) {
      return formatMessage(
        { id: 'welcome.new-user.partner.title' },
        { name: ownerFirstName, partner: dougsDisplayName }
      );
    }
    return formatMessage({ id: 'welcome.new-user.title' }, { owner_first_name: ownerFirstName });
  })();

  const subtitle = (() => {
    if (isQontoInvoicingFlow) {
      return formatMessage(
        { id: 'qonto-pa.welcome.subtitle' },
        { organization_name: organizationName }
      );
    }
    if (isCobrandedDougs && isEmbedded) {
      return formatMessage(
        { id: 'existing-company.qonto-embed.welcome-screen.subtitle.partner-name' },
        { partner: dougsDisplayName }
      );
    }
    if (isCobrandedDougs) {
      return formatMessage(
        { id: 'welcome.new-user.partner.subtitle' },
        { company_name: organizationName, partner: dougsDisplayName }
      );
    }
    if (isEmbedded) {
      return formatMessage({ id: 'existing-company.qonto-embed.welcome-screen.subtitle' });
    }
    if (isLegalPlaceFastTrack) {
      return formatMessage(
        { id: 'welcome.new-user.legalplace.subtitle' },
        { company_name: organizationName }
      );
    }
    if (isExistingCompanyFlow) {
      return formatMessage(
        { id: 'existing-company.fast-track.welcome-screen.subtitle' },
        { organization_name: organizationName }
      );
    }
    return formatMessage(
      { id: 'welcome.new-user.subtitle' },
      { organization_name: organizationName }
    );
  })();

  const featuresList = (() => {
    if (isQontoInvoicingFlow) {
      return [
        formatMessage({ id: 'qonto-pa.welcome.features.e-invoicing' }),
        formatMessage({ id: 'qonto-pa.welcome.features.invoice-sync' }),
      ];
    }
    if (isCobrandedDougs && isEmbedded) {
      return [
        formatMessage({ id: 'existing-company.qonto-embed.welcome-screen.list.item.1.qonto' }),
        formatMessage(
          { id: 'existing-company.qonto-embed.welcome-screen.list.item.2' },
          { partner: dougsDisplayName }
        ),
        formatMessage({ id: 'existing-company.qonto-embed.welcome-screen.list.item.3' }),
      ];
    }
    if (isEmbedded) {
      return [
        formatMessage(
          { id: 'existing-company.qonto-embed.welcome-screen.list.item.1' },
          { partner: partnerName }
        ),
        formatMessage(
          { id: 'existing-company.qonto-embed.welcome-screen.list.item.2' },
          { partner: partnerName }
        ),
        formatMessage({ id: 'existing-company.qonto-embed.welcome-screen.list.item.3' }),
      ];
    }
    if (isLegalPlaceFastTrack) {
      return [
        formatMessage({ id: 'welcome.new-user.legalplace.list.item.1' }),
        formatMessage({ id: 'welcome.new-user.legalplace.list.item.2' }),
        formatMessage({ id: 'welcome.new-user.legalplace.list.item.3' }),
      ];
    }
    if (isAccountant) {
      return [
        formatMessage({ id: 'welcome.new-user.accountant.list.item.1' }),
        formatMessage({ id: 'welcome.new-user.accountant.list.item.2' }),
        formatMessage({ id: 'welcome.new-user.accountant.list.item.3' }),
      ];
    }
    return [
      formatMessage({ id: 'welcome.new-user.list.item.1' }),
      formatMessage({ id: 'welcome.new-user.list.item.2' }),
      formatMessage({ id: 'welcome.new-user.list.item.3' }),
    ];
  })();

  const illustrationSrc = isQontoInvoicingFlow ? welcomeInvoicingIllustration : welcomeIllustration;

  const ctaLabel = isExistingCompanyFlow
    ? formatMessage({ id: 'existing-company.fast-track.welcome-screen.cta' })
    : formatMessage({ id: 'welcome.new-user.cta' });

  const socialProofLabel = formatMessage({ id: 'welcome.new-user.socialproof' });

  return {
    ctaLabel,
    featuresList,
    illustrationSrc,
    shouldShowPromoBanner,
    socialProofLabel,
    subtitle,
    title,
  };
}
